import { Plus, Trash2, FolderPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utils/cn'
import { isFilterGroup } from '@/types'
import type {
  FilterGroup,
  FilterCondition,
  FilterOperator,
  BooleanOp,
} from '@/types'
import type { HierarchyLevel } from '@/api/productHierarchy'
import { HierarchyValueSelect } from './HierarchyValueSelect'
import { StoreAttributeValueSelect } from './StoreAttributeValueSelect'

/** Maps a child hierarchy field to its parent field */
const HIERARCHY_PARENT_FIELD: Record<string, string> = {
  class_id: 'department_id',
  subclass_id: 'class_id',
}

/** Maps a parent hierarchy field to its child fields */
const HIERARCHY_CHILD_FIELDS: Record<string, string[]> = {
  department_id: ['class_id', 'subclass_id'],
  class_id: ['subclass_id'],
}

/**
 * Find the parent hierarchy value from sibling conditions in the same group.
 * Returns the parent ID if a single value is selected (eq with string, or in with one item).
 */
function getParentId(
  conditions: (FilterCondition | FilterGroup)[],
  childField: string
): string | null {
  const parentField = HIERARCHY_PARENT_FIELD[childField]
  if (!parentField) return null

  for (const item of conditions) {
    if (isFilterGroup(item)) continue
    const cond = item as FilterCondition
    if (cond.field === parentField) {
      if (
        cond.operator === 'eq' &&
        typeof cond.value === 'string' &&
        cond.value
      ) {
        return cond.value
      }
      if (
        cond.operator === 'in' &&
        Array.isArray(cond.value) &&
        cond.value.length === 1
      ) {
        return cond.value[0]
      }
    }
  }
  return null
}

const ALL_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'like', label: 'like' },
  { value: 'in', label: 'in' },
]

const HIERARCHY_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'in', label: 'in' },
]

const HIERARCHY_OPERATOR_SET = new Set<FilterOperator>(
  HIERARCHY_OPERATORS.map(o => o.value)
)

interface FilterExpressionBuilderProps {
  group: FilterGroup
  onChange: (group: FilterGroup) => void
  fields: string[]
  fieldLabels: Record<string, string>
  depth?: number
  hierarchyFields?: Record<string, HierarchyLevel>
  storeAttributeFields?: Set<string>
  readOnly?: boolean
}

export function FilterExpressionBuilder({
  group,
  onChange,
  fields,
  fieldLabels,
  depth = 0,
  hierarchyFields,
  storeAttributeFields,
  readOnly,
}: FilterExpressionBuilderProps) {
  const updateOp = (op: BooleanOp) => {
    onChange({ ...group, op })
  }

  const addCondition = () => {
    const newCondition: FilterCondition = {
      field: fields[0],
      operator: 'eq',
      value: '',
    }
    onChange({ ...group, conditions: [...group.conditions, newCondition] })
  }

  const addGroup = () => {
    const newGroup: FilterGroup = { op: 'AND', conditions: [] }
    onChange({ ...group, conditions: [...group.conditions, newGroup] })
  }

  const removeItem = (index: number) => {
    onChange({
      ...group,
      conditions: group.conditions.filter((_, i) => i !== index),
    })
  }

  const updateItem = (index: number, item: FilterCondition | FilterGroup) => {
    onChange({
      ...group,
      conditions: group.conditions.map((c, i) => (i === index ? item : c)),
    })
  }

  const updateConditionField = (
    index: number,
    condition: FilterCondition,
    field: string
  ) => {
    const wasHierarchy = hierarchyFields && condition.field in hierarchyFields
    const isHierarchy = hierarchyFields && field in hierarchyFields
    const wasStoreAttr = storeAttributeFields?.has(condition.field)
    const isStoreAttr = storeAttributeFields?.has(field)

    // Reset value when switching field types
    let operator = condition.operator
    let value: string | string[] = ''

    // If switching to hierarchy or store attribute field and operator is not valid, reset to eq
    if ((isHierarchy || isStoreAttr) && !HIERARCHY_OPERATOR_SET.has(operator)) {
      operator = 'eq'
    }

    // If switching from hierarchy/store attr to non-restricted, keep operator but reset value
    if ((wasHierarchy || wasStoreAttr) && !isHierarchy && !isStoreAttr) {
      value = ''
    }

    updateItem(index, { ...condition, field, operator, value })
  }

  const updateConditionOperator = (
    index: number,
    condition: FilterCondition,
    operator: FilterOperator
  ) => {
    const wasMulti = condition.operator === 'in'
    const isMulti = operator === 'in'

    // Reset value when switching between single/multi operators
    if (wasMulti !== isMulti) {
      updateItem(index, { ...condition, operator, value: isMulti ? [] : '' })
    } else {
      updateItem(index, { ...condition, operator })
    }
  }

  const updateConditionValue = (
    index: number,
    condition: FilterCondition,
    value: string | string[]
  ) => {
    const childFields = HIERARCHY_CHILD_FIELDS[condition.field]
    if (childFields) {
      // Clear dependent child conditions when parent value changes
      const updatedConditions = group.conditions.map((item, i) => {
        if (i === index) return { ...condition, value }
        if (isFilterGroup(item)) return item
        const cond = item as FilterCondition
        if (childFields.includes(cond.field)) {
          return { ...cond, value: cond.operator === 'in' ? [] : '' }
        }
        return item
      })
      onChange({ ...group, conditions: updatedConditions })
    } else {
      updateItem(index, { ...condition, value })
    }
  }

  return (
    <div
      className={cn(
        'rounded-md border border-border p-3',
        depth > 0 && 'ml-4 border-dashed',
        !readOnly && group.conditions.length === 0 && 'border-amber-400'
      )}
    >
      {/* Group header */}
      <div className="flex items-center gap-2 mb-3">
        <Select
          value={group.op}
          onValueChange={v => updateOp(v as BooleanOp)}
          disabled={readOnly}
        >
          <SelectTrigger className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AND">AND</SelectItem>
            <SelectItem value="OR">OR</SelectItem>
          </SelectContent>
        </Select>

        {!readOnly && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1 cursor-pointer"
              onClick={addCondition}
            >
              <Plus className="h-3 w-3" />
              Condition
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1 cursor-pointer"
              onClick={addGroup}
            >
              <FolderPlus className="h-3 w-3" />
              Group
            </Button>
          </>
        )}

        {depth > 0 && (
          <span className="text-xs text-muted-foreground ml-auto">
            Nested group
          </span>
        )}
      </div>

      {/* Conditions */}
      <div className="flex flex-col gap-2">
        {group.conditions.length === 0 && (
          <p
            className={cn(
              'text-xs py-2',
              readOnly ? 'text-muted-foreground' : 'text-amber-600'
            )}
          >
            {readOnly
              ? 'No conditions'
              : 'No conditions. Add a condition or group.'}
          </p>
        )}

        {group.conditions.map((item, index) => {
          if (isFilterGroup(item)) {
            return (
              <div key={index} className="relative">
                <FilterExpressionBuilder
                  group={item}
                  onChange={updated => updateItem(index, updated)}
                  fields={fields}
                  fieldLabels={fieldLabels}
                  depth={depth + 1}
                  hierarchyFields={hierarchyFields}
                  storeAttributeFields={storeAttributeFields}
                  readOnly={readOnly}
                />
                {!readOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 h-7 w-7 p-0 cursor-pointer text-muted-foreground hover:text-destructive"
                    onClick={() => removeItem(index)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            )
          }

          // Render condition row
          const condition = item as FilterCondition
          const isHierarchyField =
            hierarchyFields && condition.field in hierarchyFields
          const isStoreAttrField = storeAttributeFields?.has(condition.field)
          const operators =
            isHierarchyField || isStoreAttrField
              ? HIERARCHY_OPERATORS
              : ALL_OPERATORS

          return (
            <div key={index} className="flex items-center gap-2">
              <Select
                value={condition.field}
                onValueChange={v => updateConditionField(index, condition, v)}
                disabled={readOnly}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fields.map(f => (
                    <SelectItem key={f} value={f}>
                      {fieldLabels[f] ?? f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={condition.operator}
                onValueChange={v =>
                  updateConditionOperator(index, condition, v as FilterOperator)
                }
                disabled={readOnly}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {operators.map(op => (
                    <SelectItem key={op.value} value={op.value}>
                      {op.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isHierarchyField ? (
                <HierarchyValueSelect
                  level={hierarchyFields[condition.field]}
                  value={condition.value as string | string[]}
                  onChange={v => updateConditionValue(index, condition, v)}
                  multiple={condition.operator === 'in'}
                  parentId={getParentId(group.conditions, condition.field)}
                />
              ) : isStoreAttrField ? (
                <StoreAttributeValueSelect
                  fieldName={condition.field}
                  value={condition.value as string | string[]}
                  onChange={v => updateConditionValue(index, condition, v)}
                  multiple={condition.operator === 'in'}
                />
              ) : (
                <Input
                  className="flex-1"
                  disabled={readOnly}
                  placeholder={
                    condition.operator === 'in'
                      ? 'Comma-separated values'
                      : 'Value'
                  }
                  value={
                    Array.isArray(condition.value)
                      ? condition.value.join(', ')
                      : String(condition.value)
                  }
                  onChange={e =>
                    updateConditionValue(index, condition, e.target.value)
                  }
                />
              )}

              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 cursor-pointer text-muted-foreground hover:text-destructive"
                  onClick={() => removeItem(index)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
